/* ============================================================================
 * Movatic WMS -- useFeedback
 * Arquivo: src/mobile/hooks/useFeedback.ts
 * Autor: CesarFilho 932048016
 * ----------------------------------------------------------------------------
 * Responsabilidade:
 *  - Centralizar o feedback tatil do coletor (som + vibracao).
 *  - Primitivas separaveis (tone/haptic) para o consumidor decidir o gating;
 *    o Home gateia o som mas nao o haptico, logo a separacao e obrigatoria.
 *
 * Extraido inline do Home.tsx (ADR-0030 F5b). Comportamento identico.
 *  - click()  = beep 800Hz/80ms (== antigo useClickSound)
 *  - alarm()  = 3 beeps 1200Hz + vibrate([200,100,200,100,200]) (== antigo
 *               playAlarmAndVibrate)
 * ========================================================================== */

import { useRef } from "react";

export function useFeedback() {
    const ctxRef = useRef<AudioContext | null>(null);

    /** Toca um tom (Web Audio). Reusa um AudioContext lazy por componente. */
    const tone = (freq: number, ms: number, gain = 0.15, delay = 0) => {
        try {
            if (!ctxRef.current) {
                const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
                if (!Ctx) return;
                ctxRef.current = new Ctx();
            }
            const ctx = ctxRef.current!;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.connect(g);
            g.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = "square";
            const start = ctx.currentTime + delay;
            g.gain.setValueAtTime(gain, start);
            g.gain.exponentialRampToValueAtTime(0.001, start + ms / 1000);
            osc.start(start);
            osc.stop(start + ms / 1000);
        } catch (e) {
            /* silent fail */
        }
    };

    /** Vibracao (Android). Guard de feature; falha em silencio. */
    const haptic = (pattern: number | number[]) => {
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
            try {
                navigator.vibrate(pattern);
            } catch (e) {
                /* silent fail */
            }
        }
    };

    /** Clique curto (== antigo playClick). So som. */
    const click = () => tone(800, 80);

    /** Alarme: 3 beeps + vibracao longa (== antigo playAlarmAndVibrate). */
    const alarm = () => {
        [0, 0.18, 0.36].forEach((d) => tone(1200, 120, 0.25, d));
        haptic([200, 100, 200, 100, 200]);
    };

    /* --- Sinais do loop de recolha (ADR-0030 F5d). Distinguiveis pelo ouvido: --- */
    /** Recolha OK (parcial/unidade). Beep claro curto + tap. */
    const ok = () => {
        tone(800, 80);
        haptic(30);
    };

    /** Esgotado declarado (SHORT). Gesto valido, nao erro: tom neutro medio. */
    const short = () => {
        tone(500, 140);
        haptic(60);
    };

    /** Erro (SKU mismatch / qty invalida / rede). Tom grave aspero + buzz triplo. */
    const err = () => {
        tone(220, 200, 0.2);
        haptic([80, 40, 80]);
    };

    /** Tarefa completa (all_picked). Triade ascendente -- inconfundivel. */
    const done = () => {
        tone(660, 90, 0.2, 0);
        tone(880, 90, 0.2, 0.1);
        tone(1175, 130, 0.22, 0.2);
        haptic([60, 50, 120]);
    };

    return { tone, haptic, click, alarm, ok, short, err, done };
}
